import React from "react";
import { useTranslation } from "react-i18next";
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from "react-icons/fa";
import { init, send } from "@emailjs/browser";
import Swal from "sweetalert2";
import "./Contact.css";
import MapWithSidebar from "../components/common/MapWithSidebar";

// Init EmailJS solo si hay clave (evita errores en build/desarrollo)
if (process.env.REACT_APP_EMAILJS_PUBLIC_KEY) {
  init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
}

const Contacto = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = React.useState({
    nombre: "",
    email: "",
    telefono: "",
    mensaje: "",
  });
  const [sending, setSending] = React.useState(false);

  // Para i18n: lee valores de contacto desde traducciones
  const address = t("contact.info.address.value", "Guachipellin, Escazu, Ofibodegas Capri.");
  const phone = t("contact.info.phone.value", "+506 6045 8068");
  const email = t("contact.info.email.value", "rsantamaria@gruposantamaria.cr");
  const schedule = t("contact.info.schedule.lines", {
    returnObjects: true,
    defaultValue: ["Lunes a Viernes: 8:00 am - 5:30 pm", "Sábados: 9:00 am - 4:00 pm"],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    // Mapea tus campos a variables de plantilla EmailJS
    const templateParams = {
      name: formData.nombre, // {{name}}
      email: formData.email, // {{email}}
      telefono: formData.telefono, // {{telefono}}
      message: formData.mensaje, // {{message}}
      time: new Date().toLocaleString(), // {{time}}
    };

    try {
      await send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams
      );

      // Éxito
      Swal.fire({
        title: t("contact.alerts.successTitle", "¡Mensaje enviado!"),
        text: t("contact.alerts.successText", "Tu mensaje se ha enviado con éxito."),
        icon: "success",
        confirmButtonText: t("contact.alerts.ok", "Aceptar"),
      });

      setFormData({ nombre: "", email: "", telefono: "", mensaje: "" });
    } catch (err) {
      console.error("EmailJS error:", err);

      // Error
      Swal.fire({
        title: t("contact.alerts.errorTitle", "Error"),
        text: t(
          "contact.alerts.errorText",
          "Hubo un problema al enviar tu mensaje. Intenta nuevamente más tarde."
        ),
        icon: "error",
        confirmButtonText: t("contact.alerts.close", "Cerrar"),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className="contacto-section"
      id="contacto"
      aria-label={t("contact.aria.sectionLabel", "Sección de contacto")}
    >
      <div className="contacto-container">
        {/* ===== Encabezado ===== */}
        <h2 className="section-title">{t("contact.title", "Contáctanos")}</h2>
        <p className="section-subtitle">
          {t("contact.subtitle", "Estamos aquí para ayudarte con tus proyectos")}
        </p>

        <div className="contacto-grid">
          {/* ===== Info de contacto ===== */}
          <div className="contacto-info" aria-label={t("contact.aria.infoRegion", "Información de contacto")}>
            <div className="info-item">
              <FaMapMarkerAlt className="info-icon" aria-hidden="true" />
              <div>
                <h3>{t("contact.info.address.title", "Dirección")}</h3>
                <p>{address}</p>
              </div>
            </div>

            <div className="info-item">
              <FaPhone className="info-icon" aria-hidden="true" />
              <div>
                <h3>{t("contact.info.phone.title", "Teléfono")}</h3>
                {/* Mejora de accesibilidad: link tel: */}
                <p>
                  <a href={`tel:${phone.replace(/\s+/g, "")}`} aria-label={t("contact.aria.callUs", "Llámanos")}>
                    {phone}
                  </a>
                </p>
              </div>
            </div>

            <div className="info-item">
              <FaEnvelope className="info-icon" aria-hidden="true" />
              <div>
                <h3>{t("contact.info.email.title", "Email")}</h3>
                {/* Mejora de accesibilidad: link mailto: */}
                <p>
                  <a href={`mailto:${email}`} aria-label={t("contact.aria.writeUs", "Escríbenos por correo")}>
                    {email}
                  </a>
                </p>
              </div>
            </div>

            <div className="info-item">
              <FaClock className="info-icon" aria-hidden="true" />
              <div>
                <h3>{t("contact.info.schedule.title", "Horario")}</h3>
                {Array.isArray(schedule) ? (
                  schedule.map((line, i) => <p key={i}>{line}</p>)
                ) : (
                  <p>{schedule}</p>
                )}
              </div>
            </div>
          </div>

          {/* ===== Formulario ===== */}
          <div className="contacto-form" aria-label={t("contact.aria.formRegion", "Formulario de contacto")}>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  name="nombre"
                  placeholder={t("contact.form.placeholders.name", "Nombre completo")}
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  aria-label={t("contact.form.labels.name", "Nombre completo")}
                />
              </div>

              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder={t("contact.form.placeholders.email", "Correo electrónico")}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  aria-label={t("contact.form.labels.email", "Correo electrónico")}
                />
              </div>

              <div className="form-group">
                <input
                  type="tel"
                  name="telefono"
                  placeholder={t("contact.form.placeholders.phone", "Teléfono")}
                  value={formData.telefono}
                  onChange={handleChange}
                  aria-label={t("contact.form.labels.phone", "Teléfono")}
                />
              </div>

              <div className="form-group">
                <textarea
                  name="mensaje"
                  placeholder={t("contact.form.placeholders.message", "Tu mensaje o consulta")}
                  value={formData.mensaje}
                  onChange={handleChange}
                  required
                  aria-label={t("contact.form.labels.message", "Mensaje")}
                ></textarea>
              </div>

              <button type="submit" className="submit-btn" disabled={sending}>
                {sending ? t("contact.form.sending", "Enviando…") : t("contact.form.submit", "Enviar mensaje")}
              </button>
            </form>
          </div>
        </div>

        {/* ===== Mapa ===== */}
        <div className="mapa-container" aria-label={t("contact.aria.mapRegion", "Mapa y cobertura")}>
          <MapWithSidebar />
        </div>
      </div>
    </section>
  );
};

export default Contacto;

// =========================
// END PAGE: CONTACT
// =========================
